import { useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import Chat from "./PharmacyChatModal";
import { APP_PATH } from "../../api/backendPath";
import { isChatOpen } from "../../utils/activeChatRegistry";
import { getAccessToken } from "../../utils/socketAuth";
import useChatDocks, { DOCK_SOURCE, memoizeAsync } from "../chat/useChatDocks";

/**
 * PharmacyChatDock — app-wide manager for minimized order-chat windows (pharmacy).
 *
 * When a patient sends a message for an order (dispatched as the
 * `chat-incoming-message` window event by PharmacyDashboardLayout's new_message
 * handler), a minimized chat bubble appears in the bottom-right corner showing the
 * unread count. Clicking the bubble expands the full PharmacyChatModal for that
 * order; the modal's close button collapses it back to a bubble.
 *
 * The conversation is scoped per pharmacy ORDER. The integer order id (order.order_id)
 * is passed to the modal as `bookingId` (used for room scoping). The display id
 * "OR0019" is shown in the header as the subtitle and is derived from the integer id.
 */
const keyOf = (d) => String(d.appointmentId ?? d.roomId ?? d.patientUserId);

// Build the human-readable order id (e.g. 19 -> "OR0019"), matching the backend's
// `OR${id.padStart(4,'0')}` order_id_display format.
const toOrderDisplayId = (orderId) =>
  orderId != null ? `OR${String(orderId).padStart(4, "0")}` : "";

const authHeaders = () => ({ Authorization: `Bearer ${getAccessToken()}` });

// Patient name + avatar by user id via the chat-participant endpoint (accessible to
// pharmacists; the consultation patient/details endpoint is doctor-only). Covers general
// (non-order) patient↔pharmacy chats too.
const lookupParticipant = memoizeAsync(async (patientUserId) => {
  const res = await axios.get(
    `${process.env.REACT_APP_API_URL}${APP_PATH.chatParticipant}`,
    { headers: authHeaders(), params: { user_id: patientUserId } },
  );
  const d = res?.data?.data || res?.data || {};
  return {
    name:
      d.name ||
      [d.full_name, d.surname].filter(Boolean).join(" ").trim() ||
      null,
    avatar: d.avatar_presigned_url || d.avatarPresignedUrl || d.avatar || null,
  };
});

// The pharmacy's own order endpoint, which the gateway enriches with
// full_name/surname/avatar_presigned_url.
const lookupOrder = memoizeAsync(async (orderId) => {
  const res = await axios.get(
    `${process.env.REACT_APP_API_URL}/api/v1/pharmacy/orders/${orderId}`,
    { headers: authHeaders() },
  );
  const d = res?.data?.data || res?.data || {};
  return {
    name:
      [d.full_name, d.surname].filter(Boolean).join(" ").trim() ||
      d.name ||
      null,
    avatar: d.avatar_presigned_url || d.avatar || null,
  };
});

// Both lookups run in parallel; the order wins, the participant fills gaps.
const resolveHeader = async ({ patientUserId, appointmentId }) => {
  const [order, person] = await Promise.all([
    appointmentId ? lookupOrder(appointmentId).catch(() => null) : null,
    patientUserId ? lookupParticipant(patientUserId).catch(() => null) : null,
  ]);
  return {
    title: order?.name || person?.name || null,
    avatar: order?.avatar || person?.avatar || null,
  };
};

const PharmacyChatDock = () => {
  const { t } = useTranslation();
  const { docks, open, update, remove, expand, minimize } = useChatDocks({
    keyOf,
    resolveHeader,
  });

  const onIncoming = useCallback(
    (e) => {
      const det = e.detail || {};
      const patientUserId = det.sender_id;
      const appointmentId = det.appointment_id ?? null; // integer pharmacy order id
      const roomId = det.room_id ?? null;
      if (!patientUserId) return; // need someone to chat with
      // Already open in a page-level chat window: the message is shown there.
      if (
        isChatOpen(
          { roomId, appointmentId, senderId: patientUserId },
          { excludeSource: DOCK_SOURCE },
        )
      )
        return;
      open({
        patientUserId,
        appointmentId,
        roomId,
        title: det.sender_name || null,
        subtitle: appointmentId ? toOrderDisplayId(appointmentId) : "",
        avatar: null,
      });
    },
    [open],
  );

  useEffect(() => {
    window.addEventListener("chat-incoming-message", onIncoming);
    return () =>
      window.removeEventListener("chat-incoming-message", onIncoming);
  }, [onIncoming]);

  // Bubbles appear once their header is resolved (see useChatDocks).
  const minimized = docks.filter((d) => !d.expanded && d.ready);


  return (
    <>
      {/* Keep every dock's PharmacyChatModal mounted: hidden UI when minimized (still
          counts unread), full panel when expanded (its own fixed bottom-right styling). */}
      {docks.map((d) => {
        const id = keyOf(d);
        return (
          <Chat
            key={`chat-${id}`}
            chatSource={DOCK_SOURCE}
            isOpen={d.expanded}
            patientUserId={d.patientUserId}
            bookingId={d.appointmentId}
            title={d.title}
            subtitle={d.subtitle}
            onClose={() => minimize(id)}
            onUnreadChange={(n) => update(id, { count: n })}
          />
        );
      })}

      {/* Minimized bubbles, stacked upward in the bottom-right corner. */}
      {minimized.map((d, idx) => {
        const id = keyOf(d);
        return (
          <div
            key={`dock-${id}`}
            onClick={() => expand(id)}
            style={{
              position: "fixed",
              right: "1rem",
              bottom: `${1 + idx * 4.25}rem`,
              zIndex: 1000,
              display: "flex",
              alignItems: "center",
              gap: 10,
              background: "#0d9488",
              color: "#fff",
              padding: "8px 12px",
              borderRadius: 30,
              boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
              cursor: "pointer",
              maxWidth: 280,
            }}
            title={t("chat.dock.openChat")}
          >
            {d.count > 0 && (
              <span
                style={{
                  minWidth: 18,
                  height: 18,
                  padding: "0 4px",
                  borderRadius: 9,
                  background: "#e53935",
                  color: "#fff",
                  fontSize: 11,
                  fontWeight: 700,
                  lineHeight: "18px",
                  textAlign: "center",
                  boxShadow: "0 0 0 2px #0d9488",
                }}
              >
                {d.count > 99 ? "99+" : d.count}
              </span>
            )}
            <span
              style={{
                display: "flex",
                flexDirection: "column",
                lineHeight: 1.2,
                overflow: "hidden",
                maxWidth: 170,
              }}
            >
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {d.title || t("chat.patient")}
              </span>
              {d.subtitle ? (
                <span
                  style={{
                    fontSize: 11,
                    opacity: 0.85,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {d.subtitle}
                </span>
              ) : null}
            </span>
            <button
              type="button"
              onClick={(ev) => {
                ev.stopPropagation();
                remove(id);
              }}
              aria-label={t("chat.dock.dismiss")}
              style={{
                marginLeft: 2,
                background: "rgba(255,255,255,0.2)",
                border: "none",
                color: "#fff",
                width: 22,
                height: 22,
                borderRadius: "50%",
                cursor: "pointer",
                lineHeight: "20px",
                fontSize: 14,
                padding: 0,
              }}
            >
              ×
            </button>
          </div>
        );
      })}
    </>
  );
};

export default PharmacyChatDock;
